import { queryOptions } from '@tanstack/react-query'
import { apiGet, unwrap } from '@/shared/api'
import {
  type CustomerBoard,
  type CustomerBoardDto,
  type CustomerBoardSummary,
  type CustomerBoardSummaryDto,
  type CustomerProject,
  type CustomerProjectDto,
  type CustomerTask,
  type CustomerTaskDto,
  toCustomerBoard,
  toCustomerBoardSummary,
  toCustomerProject,
  toCustomerTask,
} from '../model/customer'
import { taskKeys } from './queries'

export const customerTaskKeys = {
  list: () => [...taskKeys.all, 'customer'] as const,
  detail: (id: string) => [...taskKeys.all, 'customer', 'detail', id] as const,
  projects: () => [...taskKeys.all, 'customer', 'projects'] as const,
  projectBoards: (projectId: string) =>
    [...taskKeys.all, 'customer', 'projects', projectId, 'boards'] as const,
  board: (boardId: string) => [...taskKeys.all, 'customer', 'boards', boardId] as const,
}

// Витрина заказчика: задачи его компании, которые сотрудник открыл клиенту.
// Фильтрует бэкенд по customer-сессии (00-architecture § 4) — клиентского отбора
// здесь нет и быть не должно.
export const customerTasksQueryOptions = queryOptions({
  queryKey: customerTaskKeys.list(),
  queryFn: async (): Promise<CustomerTask[]> => {
    const list = unwrap<CustomerTaskDto[]>(await apiGet('/customer/tasks')) ?? []
    return list.map(toCustomerTask)
  },
})

// Карточка по прямой ссылке — своя ручка витрины, а не сотрудничья: huma держит
// один тип ответа на операцию, поэтому customer-view живёт на отдельном пути.
// Непомеченная задача отвечает 404 (не 403 — сам факт её существования не
// раскрываем).
export function customerTaskQueryOptions(id: string) {
  return queryOptions({
    queryKey: customerTaskKeys.detail(id),
    queryFn: async (): Promise<CustomerTask> =>
      toCustomerTask(unwrap<CustomerTaskDto>(await apiGet(`/customer/tasks/${id}`))),
  })
}

// Проекты компании заказчика, где ему открыта хотя бы одна доска. Проект без
// открытых досок бэкенд не отдаёт вовсе — витрина не знает о его существовании.
export const customerProjectsQueryOptions = queryOptions({
  queryKey: customerTaskKeys.projects(),
  queryFn: async (): Promise<CustomerProject[]> => {
    const list = unwrap<CustomerProjectDto[]>(await apiGet('/customer/projects')) ?? []
    return list.map(toCustomerProject)
  },
})

// Чужой или закрытый проект отвечает 404, а не 403 — существование не раскрываем.
export function customerProjectBoardsQueryOptions(projectId: string) {
  return queryOptions({
    queryKey: customerTaskKeys.projectBoards(projectId),
    queryFn: async (): Promise<CustomerBoardSummary[]> => {
      const list =
        unwrap<CustomerBoardSummaryDto[]>(await apiGet(`/customer/projects/${projectId}/boards`)) ??
        []
      return list.map(toCustomerBoardSummary)
    },
  })
}

// Канбан заказчика приходит одним ответом — колонки и задачи вместе: разложить
// карточки по этапам, не зная устройства доски, иначе нечем.
export function customerBoardQueryOptions(boardId: string) {
  return queryOptions({
    queryKey: customerTaskKeys.board(boardId),
    queryFn: async (): Promise<CustomerBoard> =>
      toCustomerBoard(unwrap<CustomerBoardDto>(await apiGet(`/customer/boards/${boardId}`))),
  })
}
